import React from "react";
import { decimalToHoursMinutes, zeroToDash } from "../../../../utils/datetime";
import { payrollHoursDiffer } from "../../../../utils";

/**
 * TotalColumn component for displaying footer totals with comparison tooltips
 * Shows differences between total payout and original worker hours
 *
 * @param {Object} props - Component props.
 * @param {number} props.payoutTotal - Payout hours total displayed in the footer cell.
 * @param {number} props.workerTotal - Worker hours total used for comparison.
 * @param {number} props.managerTotal - Manager hours log total used for comparison.
 * @param {string} props.hoursType - Hours column type.
 * @param {string} props.className - Extra classes for the footer cell.
 * @param {boolean} props.hoursMatch - Whether overridden hours match calculated clock hours.
 * @returns {JSX.Element} Footer total cell with comparison tooltip.
 */
const TotalColumn = ({
  payoutTotal,
  workerTotal,
  managerTotal = 0,
  hoursType, // 'regular', 'overtime', 'doubletime', or 'total'
  className = "",
  hoursMatch = true // Whether overridden hours match calculated hours
}) => {
  const hasChange = payrollHoursDiffer(payoutTotal, workerTotal);

  // Render comparison tooltip
  const renderComparisonTooltip = () => {
    const tooltipClasses = "absolute z-10 shadow-lg text-base px-2 py-1 rounded text-xs whitespace-nowrap -top-20 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-100 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100";

    return (
      <div className={tooltipClasses}>
        <div>
          Worker Hours:{" "}
          <span className="font-semibold">
            {zeroToDash(decimalToHoursMinutes(workerTotal, true))}
          </span>
        </div>
        <div>
          Manager Hours:{" "}
          <span className="font-semibold">
            {zeroToDash(decimalToHoursMinutes(managerTotal, true))}
          </span>
        </div>
        <div>
          Payout Hours:{" "}
          <span className="font-semibold">
            {zeroToDash(decimalToHoursMinutes(payoutTotal, true))}
          </span>
        </div>
        {/* Arrow pointing down */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"></div>
      </div>
    );
  };

  // Determine border class based on hours type
  const getBorderClass = () => {
    switch (hoursType) {
      case 'regular':
        return 'border-l-[2px] border-l-gray-400';
      case 'overtime':
      case 'doubletime':
      case 'total':
        return 'border-l';
      default:
        return 'border-l';
    }
  };

  // For total column, show red if hours don't match calculated hours
  const getTextColorClass = () => {
    if (hoursType === 'total' && !hoursMatch) {
      return "text-red-600";
    }
    return hasChange ? "text-blue-600" : "";
  };

  const cellClasses = `col-span-2 bg-gray-300 h-12 py-3 px-1 border-t-2 ${getBorderClass()} border-gray-400 flex items-center justify-center text-sm font-bold relative group ${getTextColorClass()} ${className}`;

  return (
    <div className={cellClasses}>
      {zeroToDash(decimalToHoursMinutes(payoutTotal, true))}
      {renderComparisonTooltip()}
    </div>
  );
};

export default TotalColumn;